import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, BarChart3 } from 'lucide-react';
import { getClaimFinancialMetrics } from '../../api/claims';
import { formatCurrency } from '../../utils/formatters';

const FinancialMetrics = ({ claimId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['claimFinancialMetrics', claimId],
    queryFn: () => getClaimFinancialMetrics(claimId),
    enabled: !!claimId
  });

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-20 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load financial metrics: {error.message}</p>
      </div>
    );
  }

  if (!data) return null;

  const {
    financial_impact_cents,
    estimated_cost_cents,
    expected_compensation_cents,
    action_margin,
    contribution_margin,
    net_impact_cents,
    dates
  } = data;

  const MetricCard = ({ label, value, subtext, bgColor = 'bg-gray-50', textColor = 'text-gray-900', icon: Icon }) => (
    <div className={`${bgColor} rounded-lg p-4 border border-gray-200`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-600 mb-1">{label}</p>
          <p className={`text-2xl font-bold ${textColor}`}>{value}</p>
          {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
        </div>
        {Icon && <Icon className="h-6 w-6 text-gray-400" />}
      </div>
    </div>
  );

  // Determine margin colors
  const getMarginColor = (margin) => {
    if (margin === 0) return { bg: 'bg-yellow-50', text: 'text-yellow-900' };
    if (margin < 0) return { bg: 'bg-red-50', text: 'text-red-900' };
    return { bg: 'bg-green-50', text: 'text-green-900' };
  };

  const actionMarginColor = getMarginColor(action_margin);
  const netImpactColor = net_impact_cents >= 0 ? { bg: 'bg-green-50', text: 'text-green-900' } : { bg: 'bg-red-50', text: 'text-red-900' };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Financial Metrics</h3>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Financial Impact"
          value={formatCurrency(financial_impact_cents / 100)}
          subtext="Total claim value"
          bgColor="bg-blue-50"
          textColor="text-blue-900"
          icon={DollarSign}
        />
        <MetricCard
          label="Estimated Cost"
          value={formatCurrency(estimated_cost_cents / 100)}
          subtext="Cost to pursue claim"
          bgColor="bg-orange-50"
          textColor="text-orange-900"
          icon={TrendingDown}
        />
        <MetricCard
          label="Expected Compensation"
          value={formatCurrency(expected_compensation_cents / 100)}
          subtext="Anticipated recovery"
          bgColor="bg-purple-50"
          textColor="text-purple-900"
          icon={TrendingUp}
        />
        <MetricCard
          label="Net Impact"
          value={formatCurrency(net_impact_cents / 100)}
          subtext={net_impact_cents >= 0 ? "Positive return" : "Negative return"}
          bgColor={netImpactColor.bg}
          textColor={netImpactColor.text}
          icon={net_impact_cents >= 0 ? CheckCircle : AlertTriangle}
        />
      </div>

      {/* Margin Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <MetricCard
          label="Action Margin"
          value={`${action_margin.toFixed(2)}%`}
          subtext={
            action_margin === 0
              ? "No profit margin"
              : action_margin < 0
              ? "Loss-making"
              : "Profitable action"
          }
          bgColor={actionMarginColor.bg}
          textColor={actionMarginColor.text}
          icon={action_margin === 0 ? AlertTriangle : action_margin < 0 ? AlertTriangle : CheckCircle}
        />
        <MetricCard
          label="Contribution Margin"
          value={`${contribution_margin.toFixed(2)}%`}
          subtext="Contribution to overall profit"
          bgColor="bg-indigo-50"
          textColor="text-indigo-900"
          icon={BarChart3}
        />
      </div>

      {/* Key Dates */}
      {dates && (
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Key Dates</h4>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            {dates.sop_date && (
              <div>
                <p className="text-gray-600">Start of Production</p>
                <p className="font-medium text-gray-900">
                  {new Date(dates.sop_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {dates.eop_date && (
              <div>
                <p className="text-gray-600">End of Production</p>
                <p className="font-medium text-gray-900">
                  {new Date(dates.eop_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {dates.contracted_effective_date && (
              <div>
                <p className="text-gray-600">Contract Effective</p>
                <p className="font-medium text-gray-900">
                  {new Date(dates.contracted_effective_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {dates.expected_closing_date && (
              <div>
                <p className="text-gray-600">Expected Closing</p>
                <p className="font-medium text-blue-900">
                  {new Date(dates.expected_closing_date).toLocaleDateString()}
                </p>
              </div>
            )}
            {dates.expected_payment_date && (
              <div>
                <p className="text-gray-600">Expected Payment</p>
                <p className="font-medium text-green-900">
                  {new Date(dates.expected_payment_date).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Warning for 0% Action Margin */}
      {action_margin === 0 && (
        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-900">Action Margin Alert</p>
              <p className="text-sm text-yellow-800 mt-1">
                This claim has a 0% action margin, indicating that current efforts are not yielding any profit. 
                Consider reviewing the cost structure or pursuing alternative resolution strategies.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialMetrics;
